import { Link, useHistory, useLocation } from 'react-router-dom';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import Alert from '@material-ui/lab/Alert';
import Backdrop from '@material-ui/core/Backdrop';
import Button from '@material-ui/core/Button';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import React, { useCallback, useEffect, useRef } from 'react';
import Snackbar from '@material-ui/core/Snackbar';
import Typography from '@material-ui/core/Typography';
import WarningIcon from '@material-ui/icons/Warning';

import {
	CountdownTimer,
	scannerSharedStyles,
	VideoContainer,
} from '../../components/Scanner/styled';
import { customEvent } from '../../components/GoogleTagManager';
import { useActions } from '../../store/scanSession';
import { useHandleCameraPermission } from '../../utils/permissions';
import {
	useHasCameraPermission,
	useIsLoading,
	useIsToastOpen,
	useThngId,
} from '../../store/scanSession/selectors';
import { useThunk } from '../../utils/thunks';
import {
	validateQr as validateQrThunk,
	identifyQr as identifyQrThunk,
} from '../../store/scanSession/thunks';
import NoPermission from '../../components/NoPermission';
import ProgressIndicator from '../../components/ProgressIndicator';
import QRScanner from '../../components/QRScanner';
import QRThumbnail from './qr_thumbnail.svg';
import TamperTesterHeader from '../../components/TamperTesterHeader';

const useStyles = makeStyles((theme) => ({
	backdrop: {
		zIndex: theme.zIndex.drawer + 1,
		color: '#fff',
	},
}));

function Step1() {
	const { t } = useTranslation();
	const theme = useTheme();
	const history = useHistory();
	const { search } = useLocation();
	const actions = useActions();
	const classes = useStyles();
	const validateQr = useThunk(validateQrThunk);
	const identifyQr = useThunk(identifyQrThunk);
	useHandleCameraPermission();

	const cancelRef = useRef(() => {});
	const resetRef = useRef();

	const hasCameraPermission = useHasCameraPermission();
	const isToastOpen = useIsToastOpen();
	const isLoading = useIsLoading();

	const onToastClose = useCallback(
		() => actions.setToastOpen(false),
		[actions]
	);

	const thngId = useThngId();

	const searchParams = new URLSearchParams(search);
	const urlThngId = searchParams.get('thng') || undefined;

	const shouldShowCamera = !urlThngId && !thngId;

	useEffect(() => {
		if (thngId) {
			// if we have the thngId we don't want any surprise redirections
			cancelRef.current();
		}
		return () => cancelRef.current(); // we also don't want any surprise redirections after the component is unmounted
	}, [thngId]);

	useEffect(() => {
		if (urlThngId) {
			validateQr(urlThngId);
		}
	}, [urlThngId, validateQr]);

	const onTimeout = useCallback(() => {
		history.push('/step1/help');
		customEvent('time_ends_step1');
	}, [history]);

	const onStart = useCallback(({ cancel, reset }) => {
		cancelRef.current = cancel;
		resetRef.current = reset;
	}, []);

	const onFindQrCode = useCallback(
		async ({ qr: { data: shortUrl } }) => {
			identifyQr(shortUrl);
		},
		[identifyQr]
	);

	const onTimerAlert = useCallback(() => {
		customEvent('time_starts_step1');
	}, []);

	useEffect(() => {
		if ((hasCameraPermission || isToastOpen) && resetRef.current) {
			resetRef.current();
		}
		if (isToastOpen) {
			customEvent('qr_not_relevant1');
		}
	}, [hasCameraPermission, isToastOpen, resetRef]);

	if (!hasCameraPermission) {
		return <NoPermission />;
	}

	return (
		<>
			<TamperTesterHeader />

			<VideoContainer>
				{shouldShowCamera && (
					<>
						<QRScanner
							isPaused={isLoading}
							onFindCode={onFindQrCode}
							style={scannerSharedStyles}
						/>
						<CountdownTimer
							onStart={onStart}
							onTimerAlert={onTimerAlert}
							onTimeout={onTimeout}
						/>
					</>
				)}
			</VideoContainer>
			{isLoading && (
				<Backdrop className={classes.backdrop} open={isLoading}>
					<ProgressIndicator />
				</Backdrop>
			)}
			<Container
				maxWidth="sm"
				style={{
					display: 'flex',
					flexDirection: 'column',
					flex: 1,
					justifyContent: 'space-between',
				}}
			>
				<Snackbar
					open={isToastOpen}
					autoHideDuration={6000}
					onClose={onToastClose}
					anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
				>
					<Alert
						severity="error"
						variant="outlined"
						elevation={6}
						icon={<WarningIcon />}
					>
						<Typography variant="h6" color="error">
							{t('step_1_screen.alert_title')}
						</Typography>
						<Typography variant="body1">
							{t('step_1_screen.alert_body')}
						</Typography>
					</Alert>
				</Snackbar>
				<Grid
					container
					spacing={1}
					style={{
						marginTop: theme.spacing(1),
						marginBottom: theme.spacing(3),
					}}
				>
					<Grid item xs={12}>
						<Typography variant="h4" component="h2">
							{t('step_1_screen.title')}
						</Typography>
					</Grid>
					<Grid container spacing={1} direction="row" justify="flex-start">
						<Grid item sm={2}>
							<img src={QRThumbnail} alt="QR Thumbnail" />
						</Grid>
						<Grid item sm={10} style={{ flex: 1, minWidth: 176 }}>
							<Typography variant="body1">
								{t('step_1_screen.instructions')}
							</Typography>
						</Grid>
					</Grid>
				</Grid>
				<Grid container spacing={1} style={{ marginBottom: theme.spacing(4) }}>
					<Grid item xs={12}>
						<Button
							component={Link}
							to="/step1/help"
							variant="outlined"
							color="default"
							fullWidth={true}
							disableElevation={true}
						>
							{t('step_1_screen.help')}
						</Button>
					</Grid>
					<Grid item xs={12}>
						<Button
							component={Link}
							to="/"
							variant="text"
							color="primary"
							fullWidth={true}
						>
							{t('step_1_screen.cancel')}
						</Button>
					</Grid>
				</Grid>
			</Container>
		</>
	);
}

export default Step1;
